import * as React from 'react';
import { useSelector } from 'react-redux';

import { EDealFeatureCardTypes, IDealFeatureCard } from '../../../types/promoInfo';
import { makeModalTextLink } from '../../../utils/modalTextLink';
import { selectIsAccreditiveActive } from '../../../selectors/promoInfo';

const accreditiveLink = makeModalTextLink('lc', 'Аккредитив от СберБанка');
const accreditiveLink2 = makeModalTextLink('lc', 'Аккредитивом от СберБанка');
const sbrLink = makeModalTextLink('sbr', 'Сервис безопасных расчётов Циан & Tinkoff');
const selfPaymentLink = makeModalTextLink('other', 'самостоятельные расчёты');
const courierDeliveryLink = makeModalTextLink('courierDelivery', 'представитель Удостоверяющего центра');
const signMeLink = makeModalTextLink('signMe', 'Sign.Me');
const bankRepresentativeLink = makeModalTextLink('bankRepresentative', 'Представитель банка');

/* istanbul ignore next */
export const useGetFeatures = (isSbrEnabled: boolean): IDealFeatureCard[] => {
  const isAccreditiveActive = useSelector(selectIsAccreditiveActive);
  const getCalculationMethod = (): string => {
    if (isAccreditiveActive && isSbrEnabled) {
      return `${accreditiveLink}, ${sbrLink} или ${selfPaymentLink}`;
    }
    if (isAccreditiveActive) {
      return `${accreditiveLink} или ${selfPaymentLink}`;
    }
    if (sbrLink) {
      return `${sbrLink} или ${selfPaymentLink}`;
    }

    return selfPaymentLink;
  };

  const calculationMethod = getCalculationMethod();

  return React.useMemo(() => {
    return [
      {
        type: EDealFeatureCardTypes.SameDayDeal,
        title: 'День в день',
        description: 'Проведём и отправим документы в Росреестр за один день',
        image: {
          x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_types/day_to_day_1x.jpg',
          x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_types/day_to_day_2x.jpg',
        },
        modal: {
          title: 'Сделка день в день',
          description: 'Проведём и отправим документы в Росреестр за один день.',
          details: [
            {
              title: 'Заявка',
              badge: 'В среднем 20 минут',
              details: [
                'Заполните заявку до 12:30 по местному времени, чтобы провести сделку день в день.',
                'Выберите место для проведения сделки. Сделка день в день доступна <br /> в следующих городах: <a href="https://hc.cian.ru/hc/ru/articles/5157394133908" target="_blank" rel="noopener noreferrer">список городов</a>.',
                `Выберите один из способов расчётов: ${calculationMethod}.`,
                'Договор купли-продажи вы готовите самостоятельно и загружаете в форму.',
              ],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/signing_paper_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/signing_paper_2x.png',
              },
            },
            {
              title: 'Подписание документов',
              badge: 'В среднем 30 минут',
              details: [
                'Договор купли-продажи вы готовите самостоятельно.',
                `В этот же день в назначенное время и место ${courierDeliveryLink} доставит документы для выпуска электронных подписей.`,
                `Участники сделки установят приложение ${signMeLink} и подпишут в нём документы.`,
                `${bankRepresentativeLink} по видеосвязи удостоверит участников и передаст нам документ, подтверждающий участие банка в сделке.`,
              ],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/delivery_man_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/delivery_man_2x.png',
              },
            },
            {
              title: 'Безопасный расчёт',
              badge: 'В среднем 30 минут',
              details: ['Проведите взаиморасчёты выбранным способом.'],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/vault_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/vault_2x.png',
              },
            },
            {
              title: 'Регистрация в Росреестре',
              badge: 'В среднем 1 – 5 дней',
              details: ['Персональный помощник отправит документы в электронном виде в Росреестр.'],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/rosreestr_registration_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/rosreestr_registration_2x.png',
              },
            },
            {
              title: 'Перевод денег продавцу',
              badge: 'В среднем 1 день',
              details: [
                'После успешной регистрации выполните условия в зависимости <br /> от выбранного способа взаиморасчётов.',
              ],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/home_and_money_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/home_and_money_2x.png',
              },
            },
          ],
        },
      },
      {
        type: EDealFeatureCardTypes.WithAccessibility,
        title: 'С маломобильными группами населения',
        description: 'Проведём сделку удалённо',
        image: {
          x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_types/immobile_1x.jpg',
          x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_types/immobile_2x.jpg',
        },
        modal: {
          title: 'Сделка с маломобильными группами населения',
          description:
            'Мы можем доставить документы для выпуска электронных подписей каждому участнику домой или в любое удобное место. Вся сделка пройдёт полностью удалённо.',
          details: [
            {
              title: 'Заявка',
              badge: 'В среднем 20 минут',
              details: [
                'Укажите время и место, где хотели бы провести сделку.  Участникам сделки не обязательно встречаться в одном месте: они смогут подписать документы где угодно.',
                `Выберите один из способов расчётов: ${calculationMethod}.`,
                'Договор купли-продажи вы готовите самостоятельно и загружаете в форму.',
              ],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/oral_apply_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/oral_apply_2x.png',
              },
            },
            {
              title: 'Подписание документов',
              badge: 'В среднем 30 минут',
              details: [
                `В назначенное время и место ${courierDeliveryLink} доставит электронные подписи.`,
                `Участники сделки установят приложение ${signMeLink} и подпишут в нём документы.`,
                `${bankRepresentativeLink} по видеосвязи удостоверит участников и передаст нам документ, подтверждающий участие банка в сделке.`,
              ],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/delivery_man_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/delivery_man_2x.png',
              },
            },
            {
              title: 'Безопасный расчёт',
              badge: 'В среднем 30 минут',
              details: ['Проведите взаиморасчёты выбранным способом.'],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/vault_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/vault_2x.png',
              },
            },
            {
              title: 'Регистрация в Росреестре',
              badge: 'В среднем 1 – 5 дней',
              details: ['Персональный помощник отправит документы в электронном виде в Росреестр.'],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/rosreestr_registration_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/rosreestr_registration_2x.png',
              },
            },
            {
              title: 'Перевод денег продавцу',
              badge: 'В среднем 1 день',
              details: [
                'После успешной регистрации выполните условия в зависимости от <br /> выбранного способа взаиморасчётов.',
              ],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/home_and_money_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/home_and_money_2x.png',
              },
            },
          ],
        },
      },
      {
        type: EDealFeatureCardTypes.WithMortgage,
        title: 'С ипотекой любого банка',
        description: 'Проводим сделки без закладных и с электронными закладными всех банков',
        image: {
          x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_types/any_mortgage_1x.jpg',
          x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_types/any_mortgage_2x.jpg',
        },
        modal: {
          title: 'Сделка с ипотекой любого банка',
          description: 'Мы можем зарегистрировать сделку с ипотекой любого банка без бумажной закладной.',
          details: [
            {
              title: 'Заявка',
              badge: 'В среднем 30 минут',
              details: [
                'Укажите время и место, где хотели бы провести сделку.',
                `Для сделок с ипотекой недоступны сервисы взаиморасчётов от Циан. <br /> Вам нужно будет самостоятельно провести расчёты. ${
                  isAccreditiveActive
                    ? `Для сделок с ипотекой от СберБанка вы сможете воспользоваться ${accreditiveLink2}.`
                    : ''
                }`,
                'Договор купли-продажи вы готовите самостоятельно. Он должен содержать существенные условия кредитного договора. Формулировки мы вам предложим скачать.',
              ],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/home_dialogue_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/home_dialogue_2x.png',
              },
            },
            {
              title: 'Подписание документов',
              badge: 'В среднем 30 минут',
              details: [
                `В назначенное время и место ${courierDeliveryLink} доставит электронные подписи.`,
                `Участники сделки установят приложение ${signMeLink} и подпишут в нём документы.`,
                `${bankRepresentativeLink} по видеосвязи удостоверит участников и передаст нам документ, подтверждающий участие банка в сделке.`,
              ],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/delivery_man_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/delivery_man_2x.png',
              },
            },
            {
              title: 'Регистрация в Росреестре',
              badge: 'В среднем 1 – 5 дней',
              details: ['Персональный помощник отправит документы в электронном виде в Росреестр.'],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/rosreestr_registration_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/rosreestr_registration_2x.png',
              },
            },
          ],
        },
      },
    ];
  }, [isAccreditiveActive]);
};
